/** The proposal editors whose unsaved edits a navigation can leave behind. */
export type ProposalEditorStage = "active" | "drafts";

const EDITOR_PATHS: Readonly<Record<ProposalEditorStage, RegExp>> = {
  active: /^\/proposals\/active\/(?:revise|review|view)(?:\/([^/]+))?(?:\/|$)/,
  drafts: /^\/proposals\/drafts\/(?:(create)|(?:edit|review|view)(?:\/([^/]+))?)(?:\/|$)/,
};

/**
 * Names the proposal an editor path shows: its id, `"create"` for a new draft, or `""` for an
 * editor path that names none. Answers `undefined` for a path that is no editor of that stage.
 *
 * The id is lower-cased, because a GUID typed or linked in either case names the same proposal.
 */
export function proposalShownAtEditorPath(stage: ProposalEditorStage, pathname: string): string | undefined {
  const match = EDITOR_PATHS[stage].exec(pathname);
  if (!match) return undefined;
  return (match[1] ?? match[2] ?? "").toLowerCase();
}

/**
 * Whether a navigation from `currentPathname` leaves the proposal its editor shows.
 *
 * Moving between the editor's tabs, or between revise, review and view of the same proposal, keeps
 * the edits with the proposal. Moving to another proposal's editor does not: before #7070 the pages
 * let any move between their own editor paths through, so opening a second proposal from a link or a
 * notification left the first one's edits behind without a word.
 */
export function navigationLeavesProposalEditor(
  stage: ProposalEditorStage,
  currentPathname: string,
  nextPathname: string,
): boolean {
  const currentProposal = proposalShownAtEditorPath(stage, currentPathname);
  if (currentProposal === undefined) return false;
  return proposalShownAtEditorPath(stage, nextPathname) !== currentProposal;
}
